
import { supabase } from "@/integrations/supabase/client";
import { AuthResult } from "@/services/auth/types";

/**
 * Utility for making auth token requests through the auth-proxy Edge Function
 * Use this specifically for Supabase Auth API token requests to avoid CORS issues
 */
export const authenticateWithProxy = async (body: {
  email?: string;
  password?: string;
  refresh_token?: string;
  grant_type: 'password' | 'refresh_token';
}): Promise<AuthResult> => {
  try {
    console.log(`Auth proxy: Authenticating with grant_type: ${body.grant_type}`);
    
    // First attempt: Try direct proxy invocation
    const proxyResponse = await supabase.functions.invoke('auth-proxy', {
      body: JSON.stringify(body),
    });
    
    console.log("Auth proxy response:", proxyResponse);
    
    if (proxyResponse.error) {
      console.error('Error using auth proxy:', proxyResponse.error);
      throw proxyResponse.error;
    }
    
    console.log('Auth proxy: Authentication successful');
    return { data: proxyResponse.data, error: null };
  } catch (error) {
    console.error('Failed to use auth proxy:', error);
    
    // If we're in a refresh token flow, we need to handle it differently
    if (body.grant_type === 'refresh_token' && body.refresh_token) {
      try {
        console.log('Falling back to direct auth refresh...');
        // Try direct refresh as fallback
        const { data, error } = await supabase.auth.refreshSession({
          refresh_token: body.refresh_token,
        });
        
        if (error) throw error;
        return { 
          data: {
            access_token: data.session?.access_token,
            refresh_token: data.session?.refresh_token,
            user: data.user
          }, 
          error: null 
        };
      } catch (refreshError) {
        console.error('Direct refresh also failed:', refreshError);
        return { data: null, error: refreshError };
      }
    }
    
    return { data: null, error };
  }
};

/**
 * Refresh the session token using the auth proxy
 */
export const refreshSessionViaProxy = async (refreshToken: string): Promise<AuthResult> => {
  console.log('Refreshing session via proxy with token:', refreshToken.substring(0, 3) + '...');
  return await authenticateWithProxy({
    refresh_token: refreshToken,
    grant_type: 'refresh_token'
  });
};

/**
 * Sign in using the auth proxy
 */
export const signInViaProxy = async (email: string, password: string): Promise<AuthResult> => {
  console.log(`Signing in via proxy: ${email}`);
  return await authenticateWithProxy({
    email,
    password,
    grant_type: 'password'
  });
};
